import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X, Apple, Loader2 } from "lucide-react";
import { NavBar } from "../components/NavBar";
import { useAuth } from "../lib/auth";

export default function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const { isAuthed, signInWithGoogle, signInWithEmail, registerWithEmail, error } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (isAuthed) navigate(next, { replace: true });
  }, [isAuthed, navigate, next]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setFormError(null);
    setSubmitting(true);
    try {
      if (mode === "register") {
        await registerWithEmail(email.trim().toLowerCase(), password, name.trim());
      } else {
        await signInWithEmail(email.trim().toLowerCase(), password);
      }
    } catch (err) {
      setFormError(err?.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <NavBar />
      <main className="container-nc" style={{ paddingTop: 36, paddingBottom: 90, maxWidth: 480 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
          <button className="btn-icon" onClick={() => navigate(-1)} data-testid="signin-close">
            <X size={18} />
          </button>
          <span className="chip chip-brand">NarcCheck AI</span>
        </div>
        <h1 style={{ fontSize: "clamp(2rem, 5vw, 2.6rem)", fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1.1, margin: "0 0 10px" }}>
          {mode === "register" ? "Create your account." : "Welcome back."}
        </h1>
        <p style={{ color: "var(--muted)", fontSize: 15.5, lineHeight: 1.6, marginBottom: 26 }}>
          {mode === "register"
            ? "Sign up to save your scorecards and sync them across the web and the iOS app."
            : "Sign in to sync your history across every device, including the iOS app."}
        </p>

        <form onSubmit={submit} className="card card-pad" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {mode === "register" && (
            <div>
              <label className="label">Name</label>
              <input className="input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name" autoComplete="name" />
            </div>
          )}
          <div>
            <label className="label">Email</label>
            <input className="input" type="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email" autoComplete="email" />
          </div>
          <div>
            <label className="label">Password</label>
            <input
              className="input"
              type="password"
              required
              minLength={6}
              placeholder={mode === "register" ? "At least 6 characters" : "Your password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="auth-password"
              autoComplete={mode === "register" ? "new-password" : "current-password"}
            />
            {mode === "login" && (
              <div style={{ textAlign: "right", marginTop: 8 }}>
                <span className="link" style={{ fontSize: 13 }} onClick={() => navigate("/forgot-password")} data-testid="forgot-password-link">
                  Forgot password?
                </span>
              </div>
            )}
          </div>
          {formError && (
            <p style={{ color: "var(--brand)", fontSize: 13, margin: 0 }} data-testid="auth-error">
              {formError}
            </p>
          )}
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={submitting} data-testid="auth-submit">
            {submitting ? <Loader2 size={18} className="spin" /> : mode === "register" ? "Create account" : "Sign in"}
          </button>
          <div style={{ textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
            {mode === "register" ? "Already have an account? " : "New to NarcCheck? "}
            <span
              className="link"
              data-testid="auth-toggle"
              onClick={() => {
                setMode(mode === "register" ? "login" : "register");
                setFormError(null);
              }}
            >
              {mode === "register" ? "Sign in" : "Create one"}
            </span>
          </div>
        </form>

        <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "22px 0 18px" }}>
          <span className="divider" style={{ flex: 1 }} />
          <span className="eyebrow">or</span>
          <span className="divider" style={{ flex: 1 }} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {error && (
            <p style={{ color: "var(--brand)", fontSize: 13, textAlign: "center", margin: 0 }} data-testid="signin-error">
              {error}
            </p>
          )}
          <button
            className="btn btn-lg btn-block"
            onClick={() => signInWithGoogle(next)}
            data-testid="google-signin"
            style={{ background: "#fff", color: "#111" }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            Continue with Google
          </button>

          <div
            className="btn btn-ghost btn-lg btn-block"
            style={{ opacity: 0.55, cursor: "default" }}
            data-testid="apple-note"
            title="Apple Sign-In is available in the iOS app"
          >
            <Apple size={18} /> Sign in with Apple — in the iOS app
          </div>

          <button className="btn btn-block" onClick={() => navigate("/")} data-testid="signin-later" style={{ background: "transparent", color: "var(--muted)" }}>
            Back to home
          </button>
        </div>

        <p style={{ color: "var(--muted)", fontSize: 11.5, textAlign: "center", marginTop: 18, lineHeight: 1.6 }}>
          By continuing you agree to our{" "}
          <span className="link" onClick={() => navigate("/terms")} data-testid="signin-terms">Terms</span>{" "}
          and{" "}
          <span className="link" onClick={() => navigate("/privacy")} data-testid="signin-privacy">Privacy Policy</span>.
          For personal reflection only — not professional advice.
        </p>
      </main>
    </>
  );
}
